/**
 * useProjects — shared hook for fetching active (non-deleted) projects.
 *
 * Used by AppShell sidebar nav (project sub-links) and can be reused
 * by any page that needs the same list without an extra fetch.
 *
 * Projects.tsx keeps its own local state for the full page behaviour
 * (edit/archive/delete dialogs) and does NOT use this hook — the two
 * code paths are independent on purpose.
 *
 * Invalidation bus
 * ----------------
 * Call notifyProjectsChanged() after any successful project mutation
 * (create / edit / archive / restore / delete) to cause all mounted
 * useProjects consumers to refetch — same-tab reactivity without Realtime.
 *
 * unassignedCount
 * ---------------
 * The hook also fetches the count of non-deleted meetings with no project
 * assignment (project_id IS NULL) so the sidebar can show "Unassigned (N)".
 * The count refetches alongside projects whenever `tick` changes (same bus
 * trigger) and also whenever the caller passes a `routeKey` that changes
 * (e.g. on navigation, so the count stays fresh without a full reload).
 */
import { useCallback, useEffect, useState } from "react";
import { supabase } from "./supabase";
import { getDefaultWorkspace } from "./workspace";

// ---------------------------------------------------------------------------
// Module-level invalidation bus
// ---------------------------------------------------------------------------

type Subscriber = () => void;
const _subscribers = new Set<Subscriber>();

/** Signal all mounted useProjects hooks to refetch. */
export function notifyProjectsChanged(): void {
  _subscribers.forEach((fn) => fn());
}

export interface ProjectSummary {
  id: string;
  name: string;
}

export interface UseProjectsResult {
  projects: ProjectSummary[];
  unassignedCount: number;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * @param routeKey - opaque value that changes on route navigation;
 *   when it changes the hook refetches so the unassigned count stays current.
 *   Pass `useLocation().pathname + useLocation().search` from the call site.
 */
export function useProjects(routeKey?: string): UseProjectsResult {
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [unassignedCount, setUnassignedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const reload = useCallback(() => setTick((n) => n + 1), []);

  // Subscribe to the module-level invalidation bus so any call to
  // notifyProjectsChanged() triggers a refetch in this hook instance.
  useEffect(() => {
    _subscribers.add(reload);
    return () => {
      _subscribers.delete(reload);
    };
  }, [reload]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    getDefaultWorkspace()
      .then(async (ws) => {
        // Fetch projects list and unassigned count in parallel.
        const [projectsResp, countResp] = await Promise.all([
          supabase
            .from("projects")
            .select("id, name")
            .eq("workspace_id", ws.id)
            .is("deleted_at", null)
            .order("name"),
          supabase
            .from("meetings")
            .select("id", { count: "exact", head: true })
            .eq("workspace_id", ws.id)
            .is("project_id", null)
            .is("deleted_at", null),
        ]);
        return { projectsResp, countResp };
      })
      .then(({ projectsResp, countResp }) => {
        if (cancelled) return;
        if (projectsResp.error) {
          setError(projectsResp.error.message);
        } else {
          setProjects((projectsResp.data ?? []) as ProjectSummary[]);
        }
        setUnassignedCount(countResp.count ?? 0);
        setLoading(false);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  // routeKey triggers a refetch on navigation so the count stays current.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, routeKey]);

  return { projects, unassignedCount, loading, error, reload };
}
